import { Grid, Typography } from "@mui/material";
import { TitleSubTitle } from "@platformx/utilities";
import JSZip from "jszip";
import { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { read, utils } from "xlsx";
import { disabledBlockSx } from "../../common/disabledStyles";
import "./ExcelFileUpload.css";

const ExcelFileUpload = ({ item, control, updateState, data, isDisabled = false }: any) => {
  const [tableData, setTableData] = useState<Record<string, string>[]>([]);
  const [mergeInfo, setMergeInfo] = useState<any[]>([]); // Store merge information
  const [freezeInfo, setFreezeInfo] = useState<{ row: number; col: number } | null>(null); // Freeze pane info

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files?.length) {
      const file = files?.[0];
      const reader = new FileReader();

      if (!file.name.endsWith(".xlsx")) {
        console.error("Invalid file format. Please upload .xlsx file.");
        return;
      }

      reader.onload = async (event) => {
        const tempdata = event.target?.result;
        if (tempdata) {
          const workbook = read(tempdata, { type: "array" });
          const [sheetName] = workbook.SheetNames;
          const sheet = workbook.Sheets[sheetName];
          // Convert sheet to JSON and filter out empty columns
          const json = utils.sheet_to_json(sheet, {
            defval: "", // Default value for empty cells
            header: 1, // Use the first row as headers
          });
          // If JSON is an array, map over it to process rows
          const processedData = Array.isArray(json)
            ? json.map((row: any) => {
                const filteredRow: Record<string, string> = {};
                Object.keys(row).forEach((key) => {
                  if (row[key] !== "") {
                    filteredRow[key] = row[key]; // Include only non-empty values
                  }
                });
                return filteredRow;
              })
            : [];

          setTableData(processedData);

          // Extract merge information
          const merges = sheet["!merges"] || [];
          setMergeInfo(merges);

          // Extract freeze pane info by parsing XML
          const zip = await JSZip.loadAsync(tempdata);
          const sheetXml = await zip
            .file(`xl/worksheets/${sheetName.toLowerCase()}.xml`)
            ?.async("string");
          const parser = new DOMParser();
          const xmlDoc = sheetXml ? parser.parseFromString(sheetXml, "application/xml") : null;
          const pane = xmlDoc?.querySelector("sheetViews sheetView pane");
          let freezeData: any = null;
          if (pane) {
            freezeData = {
              row: pane.getAttribute("ySplit") ? parseInt(pane.getAttribute("ySplit")!, 10) : 0,
              col: pane.getAttribute("xSplit") ? parseInt(pane.getAttribute("xSplit")!, 10) : 0,
            };
            setFreezeInfo(freezeData);
          }

          // Update the state with the processed data and merge info
          updateState(item?.name, {
            tableData: processedData,
            mergeInfo: merges,
            freezeInfo: freezeData,
          });
        }
      };

      reader.readAsArrayBuffer(file);
    }
  };

  useEffect(() => {
    if (Object.values(data)?.length > 0) {
      setTableData(data?.tableData as Record<string, string>[]);
      setMergeInfo(data?.mergeInfo || []);
      setFreezeInfo(data?.freezeInfo || null);
    }
  }, [data]);

  const renderCell = (rowIndex: number, cellIndex: number, value: any) => {
    const isFrozenCol = freezeInfo?.col === 1; // Check if the column is frozen
    // Check if the cell is part of a merge
    const merge = mergeInfo.find(
      (tmerge) => tmerge.s.r === rowIndex && tmerge.s.c === cellIndex, // Start of merge
    );

    let cellContent: ReactNode;
    if (
      (typeof value === "string" &&
        (value.toLowerCase() === "yes" || value.toLowerCase() === "true")) ||
      (typeof value === "boolean" && value === true)
    ) {
      cellContent = <span className='check-icon'>✔</span>;
    } else if (
      (typeof value === "string" &&
        (value.toLowerCase() === "no" || value.toLowerCase() === "false")) ||
      (typeof value === "boolean" && value === false)
    ) {
      cellContent = <span className='cross-icon'>✘</span>;
    } else {
      cellContent = value;
    }

    if (merge) {
      const rowSpan = merge.e.r - merge.s.r + 1; // Calculate row span
      const colSpan = merge.e.c - merge.s.c + 1; // Calculate column span
      return (
        <td
          rowSpan={rowSpan}
          colSpan={colSpan}
          key={`${rowIndex}-${cellIndex}`}
          className={cellIndex === 0 && isFrozenCol ? "freeze-cell" : ""}
          style={{
            textAlign: "center",
            whiteSpace: "nowrap", // Prevent breaking into multiple lines
            width: cellIndex === 0 ? "150px" : "auto",
          }}>
          {cellContent}
        </td>
      );
    }
    return (
      <td
        key={`${rowIndex}-${cellIndex}`}
        className={cellIndex === 0 && isFrozenCol ? "freeze-cell" : ""}
        style={{
          textAlign: "center",
          whiteSpace: "nowrap", // Prevent breaking into multiple lines
          width: cellIndex === 0 ? "150px" : "auto",
        }}>
        {cellContent}
      </td>
    );
  };

  return (
    <Grid container sx={disabledBlockSx(isDisabled)}>
      <Grid item xs={12} sm={5} md={5} lg={5} className='leftFiled'>
        <TitleSubTitle
          titleVariant='h6medium'
          subTitleVariant='p4regular'
          title={item?.validations?.[0]?.type === "required" ? `${item?.title}*` : item?.title}
          subTitle={item?.description}
        />
      </Grid>
      <Grid item xs={12} sm={7} md={7} lg={7} className='textFiled'>
        <Controller
          name={item.name}
          control={control}
          defaultValue={null}
          render={() => (
            <input type='file' accept='.xlsx, .xls' onChange={(e) => handleFileUpload(e)} />
          )}
        />
        <Typography variant='p4regular'>
          *By default, the first row will be considered the header.
        </Typography>
      </Grid>
      <Grid item xs={12} className='table-header'>
        {tableData?.length > 0 && (
          <div className='table-container'>
            <table className='styled-table'>
              <thead>
                <tr>
                  {Object.values(tableData?.[0])?.map((header, index) => (
                    <th
                      className={
                        freezeInfo?.row === 1 || (freezeInfo?.col === 1 && index === 0)
                          ? "freeze-cell"
                          : ""
                      }
                      key={header}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className='table-body-container'>
                {tableData?.slice(1)?.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {Object.values(row)?.map((value, cellIndex) =>
                      renderCell(rowIndex + 1, cellIndex, value),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Grid>
    </Grid>
  );
};

export default ExcelFileUpload;
